import React from 'react';
import { formatTimeShort } from '../utils/formatters';
import './LapList.css'; // Make sure to create this file

interface Segment {
  distance: number;
  pace: number | null;
}

interface Lap {
  lapNumber: number;
  lapTime: number;
  splitTime: number;
  pace: number | null;
  segments?: Segment[];
  totalDistance?: number;
}

interface LapListProps {
  lapData: Lap[];
  showAllSegments?: boolean;
  formatPace: (pace: number, unit: string) => string;
  onToggleSegments: () => void;
  paceUnit?: string;
}

const LapList: React.FC<LapListProps> = ({
  lapData,
  showAllSegments = false,
  formatPace,
  onToggleSegments,
  paceUnit = 'mile',
}) => {
  if (!lapData || lapData.length === 0) {
    return <div className="lap-display__empty">No laps recorded yet</div>;
  }

  // Render segments for a lap
  // This function is used within the JSX when showAllSegments is true
  const renderSegments = (
    segments?: { distance: number; pace: number | null }[]
  ): React.ReactNode => {
    if (!segments || segments.length === 0) {
      return null;
    }

    return (
      <div className="lap-segments-container">
        <table className="lap-segments-table">
          <thead>
            <tr>
              <th>Distance</th>
              <th>Pace</th>
            </tr>
          </thead>
          <tbody>
            {segments.map((segment, index) => (
              <tr key={index} className="lap-segment-row">
                <td className="lap-segment-distance">{segment.distance}m</td>
                <td className="lap-segment-pace">
                  {segment.pace !== null && typeof formatPace === 'function'
                    ? formatPace(segment.pace, paceUnit)
                    : '--:--'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="lap-display__list">
      {/* Column Headers */}
      <table className="lap-display__table">
        <thead>
          <tr>
            <th className="lap-display__header-lap">Lap</th>
            <th className="lap-display__header-time">Lap Time</th>
            <th className="lap-display__header-split">Total Time</th>
            <th className="lap-display__header-pace">Pace</th>
          </tr>
        </thead>
        <tbody>
          {lapData.map(lap => (
            <tr key={`lap-${lap.lapNumber}`} className="lap-display__row">
              <td className="lap-display__lap-number">Lap {lap.lapNumber}</td>
              <td className="lap-display__lap-time">{formatTimeShort(lap.lapTime)}</td>
              <td className="lap-display__split-time">{formatTimeShort(lap.splitTime)}</td>
              <td className="lap-display__pace">
                {lap.pace ? formatPace(lap.pace, paceUnit) : '--:--'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Segments Display */}
      {showAllSegments && (
        <div className="lap-display__segments-container">
          {lapData.map(lap =>
            lap.segments && lap.segments.length > 0 ? (
              <div key={`segments-${lap.lapNumber}`} className="lap-display__lap-segments">
                <h4 className="lap-display__segments-header">Lap {lap.lapNumber} Segments</h4>
                {renderSegments(lap.segments)}
              </div>
            ) : null
          )}
        </div>
      )}

      {lapData.some(lap => lap.segments && lap.segments.length > 0) && (
        <button onClick={onToggleSegments} className="lap-display__toggle-button">
          {showAllSegments ? 'Hide segments' : 'Show all segments'}
        </button>
      )}
    </div>
  );
};

export default LapList;
